import { generateNKeysBetween } from "fractional-indexing";
import { useState } from "react";

import {
  sortRules,
  useActivePageId,
  useConditionalFormattingCache,
  useDeleteConditionalFormattingRules,
  usePageProductByGridId,
  useProductById,
  useUpsertConditionalFormattingRules,
  type TConditionalFormattingRule,
} from "../../../../data";
import { cellInside, rangeLabel, SelectionInfo } from "../SelectionInfo";
import { moveGroupDown, moveGroupUp } from "../../../../utils/sorting";
import type { TRangeSelection } from "../../useRangeSelectionChange";
import { strictRowStringToCode } from "@package/grid-utils";
import { useGrid } from "../../stores";

import { ConditionalFormattingRuleRow } from "./Rule";
import { ActionBar } from "./ActionBar";
import { targetCells } from "../util";
import { isNullish } from "remeda";
import { useOnCancel, useOnSave } from "../ModalContainer";
import { ManageTable, RangeSelect } from "../Shared";
import { Checkbox } from "../../../ui";
import { rowIdToPeriod } from "../../../../data/formatting/helpers";

export const ManageConditionalFormatting = ({
  selection,
  rules,
}: {
  selection?: TRangeSelection;
  rules: TConditionalFormattingRule[];
}) => {
  const [filter, setFilter] = useState<"page" | "range">("range");
  const [selected, setSelected] = useState<string[]>([]);
  const [deleted, setDeleted] = useState<string[]>([]);
  const pageId = useActivePageId();
  const [gridApi] = useGrid();

  const remove = useDeleteConditionalFormattingRules(pageId);
  const upsert = useUpsertConditionalFormattingRules(pageId);
  const cache = useConditionalFormattingCache(pageId);

  const [values, setValues] = useState(rules.map((x) => ({ ...x, changed: false })));

  const visible = sortRules(
    values.filter((rule) => {
      if (deleted.includes(rule.id)) return false;
      if (filter === "page") return true;
      return cellInside(selection ?? [], rule.column_id, rowIdToPeriod(rule.row_id));
    }),
  );

  const onUpdate = (arg: Partial<TConditionalFormattingRule> & { id: string }) => {
    setValues((values) => {
      const next = values.map((x) => (x.id === arg.id ? { ...x, ...arg, changed: true } : x));
      setValues(next);
      cache.set(next);
      refreshCells();
      return next;
    });
  };

  const onRemove = () => {
    setSelected([]);
    setDeleted((prev) => [...prev, ...selected]);
  };

  const onMoveUp = () => {
    const indices = selected.map((x) => visible.findIndex((y) => y.id === x));
    const next = moveGroupUp(sortRules(visible), indices);
    const first = next[0].priority_idx;
    const rest = generateNKeysBetween(null, first, next.length - 1).reverse();
    const keys = [first, ...rest];
    next.forEach((x, i) => onUpdate({ id: x.id, priority_idx: keys[i] }));
  };

  const onMoveDown = () => {
    const indices = selected.map((x) => visible.findIndex((y) => y.id === x));
    const next = moveGroupDown(sortRules(visible), indices);
    const first = next[0].priority_idx;
    const rest = generateNKeysBetween(null, first, next.length - 1).reverse();
    const keys = [first, ...rest];
    next.forEach((x, i) => onUpdate({ id: x.id, priority_idx: keys[i] }));
  };

  useOnSave(async () => {
    const updates = values
      .filter((x) => x.changed && !deleted.includes(x.id))
      .map((x) => ({ ...x, changed: undefined }));

    for (const update of updates) {
      if (update.limit?.trim() === "" || isNullish(update.limit)) {
        throw new Error("All rules must have a limit");
      }
    }

    await Promise.all([deleted.length && remove.mutateAsync(deleted), updates.length && upsert.mutateAsync(updates)]);

    refreshCells();
  });

  const refreshCells = () => {
    const columns = rules.map((x) => x.column_id).filter(Boolean);
    const rowNodes = rules
      .map((x) => {
        const period = rowIdToPeriod(x.row_id);
        if (!period) return null;
        const rowCode = strictRowStringToCode(period);
        if (!rowCode) return null;
        return gridApi?.getRowNode(rowCode);
      })
      .filter(Boolean);
    gridApi?.refreshCells({ columns, rowNodes, force: true });
  };

  useOnCancel(() => {
    cache.set(rules);
    refreshCells();
  });

  return (
    <>
      <SelectionInfo>{rangeLabel(selection ?? [])}</SelectionInfo>
      <RangeSelect value={filter} onValueChange={(value) => setFilter(value)} />

      <ManageTable className="[grid-template:auto/2.5rem_1px_104px_100px_108px_auto_auto_auto]">
        <ManageTable.Header>
          <div className="flex justify-center">
            <Checkbox
              data-testid="select-all-checkbox"
              checked={visible.length > 0 && selected.length === visible.length}
              onChange={(e) => setSelected(e.target.checked ? visible.map((x) => x.id) : [])}
            />
          </div>
          <div
            className="border-r border-outline h-[23px]"
            style={{ marginRight: "var(--spacing-scale-small-200, 6px)" }}
          />
          <div className="flex items-center">Affected</div>
          <div className="flex items-center">Rule</div>
          <div className="flex items-center">Limit</div>
          <div className="flex items-center">Format</div>
          <div className="flex items-center">Notes</div>
          <div className="truncate flex items-center">Stop if true</div>
        </ManageTable.Header>
        {visible.map((rule) => (
          <ConditionalFormattingRuleRow
            key={rule.id}
            pageId={pageId ?? ""}
            value={rule}
            selected={selected.includes(rule.id)}
            affected={<RuleAffectedLabel rule={rule} pageId={pageId ?? ""} />}
            onSelect={() =>
              setSelected((prev) => (prev.includes(rule.id) ? prev.filter((x) => x !== rule.id) : [...prev, rule.id]))
            }
            onUpdate={(c) => onUpdate({ id: rule.id, ...c })}
            limitWidth={108}
          />
        ))}
      </ManageTable>
      <ActionBar
        disabled={selected.length === 0}
        onRemove={onRemove}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        onTarget={() =>
          targetCells(
            gridApi,
            visible.filter((x) => selected.includes(x.id)),
          )
        }
      />
    </>
  );
};

const RuleAffectedLabel = ({
  rule: { column_id, row_id },
  pageId,
}: {
  rule: TConditionalFormattingRule;
  pageId: string;
}) => {
  const pageProduct = usePageProductByGridId(pageId, column_id);
  const product = useProductById(pageProduct.data?.product_id ?? "");
  return `${product.data?.name} [${rowIdToPeriod(row_id)}]`;
};
